import type { ReactNode } from 'react'
import { LayerDialog } from '@cloudflare/kumo'
import { useTranslation } from '../i18n'

export interface ConfirmDialogProps {
  /** Whether the dialog is open. */
  open: boolean
  /** Callback when the open state changes (cancel, close, or Esc). */
  onOpenChange: (open: boolean) => void
  /** Dialog title. */
  title: ReactNode
  /** Optional supporting copy rendered under the title. */
  description?: ReactNode
  /** Confirm button label. */
  confirmLabel: ReactNode
  /** Cancel button label (defaults to `t('common.cancel')`). */
  cancelLabel?: string
  /** Confirm button variant (default: `'destructive'`). */
  variant?: 'destructive' | 'primary'
  /** Whether the confirm action is in progress. */
  loading?: boolean
  /** Callback when the confirm button is clicked. */
  onConfirm: () => void | Promise<void>
}

/**
 * Small alert-dialog confirmation, replacing `window.confirm` across the
 * admin SPA. Unlike `DeleteResource`, this has no typed-confirmation input —
 * it's for simple yes/no confirmations (cancel booking, deactivate company,
 * regenerate a credential, etc.), not typed-name delete flows.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel,
  variant = 'destructive',
  loading = false,
  onConfirm,
}: ConfirmDialogProps) {
  const { t } = useTranslation()

  return (
    <LayerDialog.Alert
      open={open}
      onOpenChange={(next) => onOpenChange(next)}
      dismissDisabled={loading}
    >
      <LayerDialog.Content size="sm">
        <LayerDialog.Title>{title}</LayerDialog.Title>
        <LayerDialog.Body>
          {description ? <p className="text-sm text-kumo-subtle">{description}</p> : null}
        </LayerDialog.Body>
        <LayerDialog.Actions dismissLabel={cancelLabel ?? t('common.cancel')}>
          <LayerDialog.Action
            variant={variant}
            loading={loading}
            disabled={loading}
            onClick={() => void onConfirm()}
          >
            {confirmLabel}
          </LayerDialog.Action>
        </LayerDialog.Actions>
      </LayerDialog.Content>
    </LayerDialog.Alert>
  )
}

ConfirmDialog.displayName = 'ConfirmDialog'
